import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Truck, 
  ShoppingCart, Clock, AlertTriangle, CheckCircle2, Users,
  BarChart2, Calendar, MapPin, ArrowRight, Eye, Target,
  Activity, Zap, Shield, Globe, Database, RefreshCw,
  ArrowUp, ArrowDown
} from 'lucide-react';

const OverviewTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Quick Stats (moved from main dashboard)
  const quickStats = [
    {
      id: 'total-spend',
      label: 'Total Spend',
      value: 'SAR 12.4M',
      change: '+18.2%',
      trend: 'up',
      icon: DollarSign,
      color: 'emerald'
    },
    {
      id: 'active-pos',
      label: 'Active POs',
      value: '156',
      change: '+12%',
      trend: 'up',
      icon: ShoppingCart,
      color: 'blue'
    },
    {
      id: 'on-time-delivery',
      label: 'On-Time Delivery',
      value: '94.2%',
      change: '+2.1%',
      trend: 'up',
      icon: CheckCircle2,
      color: 'green'
    },
    {
      id: 'inventory-turnover',
      label: 'Inventory Turnover',
      value: '8.5x',
      change: '+0.3x',
      trend: 'up',
      icon: RefreshCw,
      color: 'purple'
    }
  ];

  // Executive KPIs
  const executiveKPIs = [
    {
      id: 'total-spend',
      label: 'Total Spend',
      value: 'SAR 12.4M',
      change: '+18.2%',
      trend: 'up',
      icon: DollarSign,
      color: 'emerald',
      detail: 'vs last month'
    },
    {
      id: 'cost-savings',
      label: 'Cost Savings',
      value: 'SAR 2.1M',
      change: '+12.5%',
      trend: 'up',
      icon: TrendingUp,
      color: 'green',
      detail: 'YTD achieved'
    },
    {
      id: 'supplier-performance',
      label: 'Supplier Performance',
      value: '94.2%',
      change: '+2.1%',
      trend: 'up',
      icon: Target,
      color: 'blue',
      detail: 'on-time delivery'
    },
    {
      id: 'inventory-efficiency',
      label: 'Inventory Efficiency',
      value: '8.5x',
      change: '+0.3x',
      trend: 'up',
      icon: RefreshCw,
      color: 'purple',
      detail: 'turnover rate'
    }
  ];

  // Critical Alerts
  const criticalAlerts = [
    {
      id: 1,
      type: 'high',
      title: 'Low Stock Alert - Raw Materials',
      description: '5 critical items below safety stock levels',
      time: '2 hours ago',
      category: 'inventory'
    },
    {
      id: 2,
      type: 'medium',
      title: 'Delayed Shipment - Electronics',
      description: 'PO-2024-089 delayed by 3 days',
      time: '4 hours ago',
      category: 'logistics'
    },
    {
      id: 3,
      type: 'high',
      title: 'Quality Issue - Packaging',
      description: 'Batch QC-2024-156 failed inspection',
      time: '6 hours ago',
      category: 'quality'
    }
  ];

  // Recent Activities
  const recentActivities = [
    {
      id: 1,
      action: 'PO Approved',
      description: 'Purchase Order PO-2024-092 approved by Finance',
      time: '15 minutes ago',
      user: 'Sarah Ahmed',
      category: 'procurement'
    },
    {
      id: 2,
      action: 'Shipment Received',
      description: 'Container SC-2024-045 received at Warehouse A',
      time: '1 hour ago',
      user: 'Mohammed Ali',
      category: 'logistics'
    },
    {
      id: 3,
      action: 'New Request Created',
      description: 'Purchase request PR-2024-187 for IT equipment',
      time: '2 hours ago',
      user: 'Fatima Hassan',
      category: 'requests'
    },
    {
      id: 4,
      action: 'Vendor Contract Renewed',
      description: 'Annual contract renewed with Tech Solutions Ltd',
      time: '3 hours ago',
      user: 'Ahmed Omar',
      category: 'procurement'
    }
  ];

  // Performance Metrics by Category
  const categoryMetrics = [
    {
      category: 'Procurement',
      icon: ShoppingCart,
      color: 'blue',
      metrics: [
        { label: 'Active POs', value: '156', trend: '+12%' },
        { label: 'Avg Lead Time', value: '14 days', trend: '-2 days' },
        { label: 'Cost Savings', value: 'SAR 890K', trend: '+15%' }
      ]
    },
    {
      category: 'Logistics',
      icon: Truck,
      color: 'green',
      metrics: [
        { label: 'On-Time Delivery', value: '94.2%', trend: '+2.1%' },
        { label: 'In Transit', value: '89 shipments', trend: '+8%' },
        { label: 'Freight Cost', value: 'SAR 1.2M', trend: '-5%' }
      ]
    },
    {
      category: 'Warehouse',
      icon: Package,
      color: 'purple',
      metrics: [
        { label: 'Space Utilization', value: '78%', trend: '+3%' },
        { label: 'Pick Accuracy', value: '99.1%', trend: '+0.2%' },
        { label: 'Stock Value', value: 'SAR 2.4M', trend: '+8%' }
      ]
    },
    {
      category: 'Planning',
      icon: Target,
      color: 'orange',
      metrics: [
        { label: 'Forecast Accuracy', value: '87%', trend: '+4%' },
        { label: 'Demand Fulfillment', value: '96%', trend: '+1%' },
        { label: 'Planning Cycle', value: '7 days', trend: '-2 days' }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-600 text-white',
      green: 'bg-green-600 text-white',
      orange: 'bg-orange-600 text-white',
      purple: 'bg-purple-600 text-white',
      red: 'bg-red-600 text-white',
      indigo: 'bg-indigo-600 text-white',
      emerald: 'bg-emerald-600 text-white'
    };
    return colors[color] || colors.blue;
  };

  const getAlertColor = (type) => {
    return type === 'high' ? 'bg-red-600 text-white' : 'bg-amber-600 text-white';
  };

  const getCategoryColor = (category) => {
    const colors = {
      procurement: 'bg-blue-600 text-white',
      logistics: 'bg-green-600 text-white',
      inventory: 'bg-purple-600 text-white',
      quality: 'bg-red-600 text-white',
      requests: 'bg-cyan-600 text-white'
    };
    return colors[category] || 'bg-gray-600 text-white';
  };

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Executive Overview</h2>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat) => (
          <motion.div
            key={stat.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-xl ${getColorClasses(stat.color)} bg-opacity-10`}>
                  <stat.icon className={`w-6 h-6 ${getColorClasses(stat.color).split(' ')[1]}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className={`flex items-center text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.trend === 'up' ? (
                  <ArrowUp className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDown className="w-4 h-4 mr-1" />
                )}
                {stat.change}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Executive KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {executiveKPIs.map((kpi) => (
          <motion.div
            key={kpi.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-${kpi.color}-600 text-white`}>
                <kpi.icon className="w-6 h-6" />
              </div>
              <div className={`flex items-center text-sm font-medium ${
                kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {kpi.trend === 'up' ? (
                  <TrendingUp className="w-4 h-4 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 mr-1" />
                )}
                {kpi.change}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{kpi.value}</h3>
            <p className="text-sm text-gray-500 mb-1">{kpi.label}</p>
            <p className="text-xs text-gray-400">{kpi.detail}</p>
          </motion.div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Alerts */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Critical Alerts</h3>
                <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                  {criticalAlerts.length} Active
                </span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {criticalAlerts.map((alert) => (
                <motion.div
                  key={alert.id}
                  whileHover={{ scale: 1.01 }}
                  className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(alert.type)}`}>
                      {alert.type.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">{alert.time}</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">{alert.title}</h4>
                  <p className="text-xs text-gray-600 mb-2">{alert.description}</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(alert.category)}`}>
                    {alert.category}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
                <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                  View All
                </button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {recentActivities.map((activity) => (
                <motion.div
                  key={activity.id}
                  whileHover={{ scale: 1.01 }}
                  className="flex items-center p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors"
                >
                  <div className={`p-2 rounded-lg ${getCategoryColor(activity.category)} mr-4`}>
                    <Activity className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium text-gray-900">{activity.action}</h4>
                      <span className="text-xs text-gray-500">{activity.time}</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{activity.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">by {activity.user}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(activity.category)}`}>
                        {activity.category}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics by Category */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Performance by Category</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categoryMetrics.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.category} className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl bg-${category.color}-600 text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{category.category}</h4>
                      <p className="text-sm text-gray-500">Performance metrics</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {category.metrics.map((metric, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">{metric.label}</span>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">{metric.value}</div>
                          <div className="text-xs text-green-600">{metric.trend}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Create PO', icon: ShoppingCart, color: 'blue' },
              { label: 'View Reports', icon: BarChart2, color: 'green' },
              { label: 'Track Shipments', icon: Truck, color: 'purple' },
              { label: 'Inventory Check', icon: Package, color: 'orange' }
            ].map((action, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
              >
                <div className={`p-3 rounded-xl bg-${action.color}-600 text-white mb-3`}>
                  <action.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium text-gray-900">{action.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab; 